<template>
  <div class="page-wrapper">
    <!-- Background orbs -->
    <div class="bg-orb bg-orb-1"></div>
    <div class="bg-orb bg-orb-2"></div>
    <div class="bg-orb bg-orb-3"></div>

    <!-- Floating particles -->
    <div class="particles">
      <span v-for="n in 12" :key="n" class="particle" :style="particleStyle(n)"></span>
    </div>

    <div class="content">
      <!-- 404 Number -->
      <div class="number-wrap">
        <span class="four left">4</span>
        <div class="zero-wrap">
          <span class="zero">0</span>
          <div class="zero-ring"></div>
        </div>
        <span class="four right">4</span>
      </div>

      <!-- Divider line -->
      <div class="divider">
        <span class="divider-line"></span>
        <span class="divider-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        </span>
        <span class="divider-line"></span>
      </div>

      <h2 class="title">Page Not Found</h2>
      <p class="subtitle">The page you're looking for doesn't exist or has been moved.</p>

      <button class="back-btn" @click="goBack">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
        Go Back
      </button>
    </div>
  </div>
</template>

<script>
export default {
  methods: {
    goBack() {
      this.$router.go(-1);
    },
    particleStyle(n) {
      const sizes  = [3, 4, 5, 3, 6, 4, 3, 5, 4, 3, 6, 4];
      const top    = [10, 20, 35, 55, 70, 80, 15, 45, 60, 75, 25, 88];
      const left   = [5, 15, 80, 90, 8, 70, 50, 35, 92, 22, 64, 48];
      const delays = [0, 1.2, 2.4, 0.6, 3.1, 1.8, 4.0, 2.7, 0.3, 3.6, 1.5, 2.1];
      const dur    = [4, 5, 6, 4.5, 5.5, 4, 6, 5, 4.5, 5, 6, 4];
      const i = n - 1;
      return {
        width:  `${sizes[i]}px`,
        height: `${sizes[i]}px`,
        top:    `${top[i]}%`,
        left:   `${left[i]}%`,
        animationDelay:    `${delays[i]}s`,
        animationDuration: `${dur[i]}s`,
      };
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@700&family=Jost:wght@300;400;500;600;700&display=swap');

* { box-sizing: border-box; margin: 0; padding: 0; }

/* ── Page ── */
.page-wrapper {
  position: relative;
  min-height: 100vh;
  background: #0f1923;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Jost', sans-serif;
  overflow: hidden;
}

/* ── Background Orbs ── */
.bg-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(90px);
  pointer-events: none;
}
.bg-orb-1 {
  width: 550px; height: 550px;
  background: radial-gradient(circle, rgba(200,169,110,0.14), transparent);
  top: -200px; right: -100px;
}
.bg-orb-2 {
  width: 420px; height: 420px;
  background: radial-gradient(circle, rgba(58,95,138,0.18), transparent);
  bottom: -100px; left: -80px;
}
.bg-orb-3 {
  width: 300px; height: 300px;
  background: radial-gradient(circle, rgba(200,169,110,0.07), transparent);
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
}

/* ── Particles ── */
.particles { position: absolute; inset: 0; pointer-events: none; }
.particle {
  position: absolute;
  border-radius: 50%;
  background: rgba(200, 169, 110, 0.45);
  animation: floatParticle linear infinite;
}
@keyframes floatParticle {
  0%   { transform: translateY(0)   scale(1);   opacity: 0; }
  20%  { opacity: 1; }
  80%  { opacity: 0.6; }
  100% { transform: translateY(-80px) scale(0.6); opacity: 0; }
}

/* ── Content ── */
.content {
  position: relative;
  z-index: 1;
  text-align: center;
  padding: 2rem 1.5rem;
  animation: fadeIn 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── 404 Numbers ── */
.number-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.1em;
  line-height: 1;
  margin-bottom: 0.5rem;
}
.four, .zero {
  font-family: 'Jost', sans-serif;
  font-size: clamp(100px, 20vw, 200px);
  font-weight: 700;
  color: transparent;
  -webkit-text-stroke: 2px rgba(200, 169, 110, 0.5);
  letter-spacing: -4px;
  user-select: none;
}
.four.left  { animation: nudgeLeft  3s ease-in-out infinite; }
.four.right { animation: nudgeRight 3s ease-in-out infinite; }
@keyframes nudgeLeft  { 0%,100%{ transform:translateX(0); } 50%{ transform:translateX(-6px); } }
@keyframes nudgeRight { 0%,100%{ transform:translateX(0); } 50%{ transform:translateX(6px); } }

.zero-wrap {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}
.zero {
  -webkit-text-stroke: 2px #c8a96e;
  color: transparent;
  text-shadow: 0 0 60px rgba(200,169,110,0.25);
}
.zero-ring {
  position: absolute;
  width: clamp(80px, 14vw, 160px);
  height: clamp(80px, 14vw, 160px);
  border-radius: 50%;
  border: 1px solid rgba(200, 169, 110, 0.2);
  animation: spin 12s linear infinite;
}
.zero-ring::before {
  content: '';
  position: absolute;
  top: -3px; left: 50%;
  transform: translateX(-50%);
  width: 6px; height: 6px;
  background: #c8a96e;
  border-radius: 50%;
  box-shadow: 0 0 8px #c8a96e;
}
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

/* ── Divider ── */
.divider {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  max-width: 300px;
  margin: 0.25rem auto 1.5rem;
}
.divider-line {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(200,169,110,0.4), transparent);
}
.divider-icon {
  color: rgba(200,169,110,0.6);
  display: flex;
}
.divider-icon svg { width: 14px; height: 14px; }

/* ── Text ── */
.title {
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(1.5rem, 5vw, 2.25rem);
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.02em;
  margin-bottom: 0.65rem;
}
.subtitle {
  font-size: 0.9rem;
  color: rgba(255,255,255,0.45);
  font-weight: 300;
  letter-spacing: 0.03em;
  margin-bottom: 2.25rem;
}

/* ── Button ── */
.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.75rem 1.75rem;
  background: transparent;
  color: #c8a96e;
  font-family: 'Jost', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border: 1.5px solid rgba(200,169,110,0.45);
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s, color 0.2s, transform 0.15s, box-shadow 0.2s;
}
.back-btn svg { width: 15px; height: 15px; transition: transform 0.2s; }
.back-btn:hover {
  background: rgba(200,169,110,0.1);
  border-color: #c8a96e;
  color: #e8c98a;
  transform: translateY(-1px);
  box-shadow: 0 6px 24px rgba(200,169,110,0.15);
}
.back-btn:hover svg { transform: translateX(-3px); }
</style>